import { useState, useEffect } from "react";
import { Link2, TrendingUp, Calendar } from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import usePageTitle from "@/hooks/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#AE132A", "#09090B", "#52525b", "#a1a1aa", "#d4d4d8", "#e4e4e7"];

export default function DashboardPage() {
  usePageTitle("Dashboard");
  const [stats, setStats] = useState({
    total_links: 0,
    source_distribution: [],
    links_last_7_days: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { weekday: "short" });
  };

  // Get top source
  const topSource = stats.source_distribution[0];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-zinc-950 text-white px-3 py-2 text-sm">
          <p className="font-medium">{label || payload[0].name}</p>
          <p className="text-zinc-300">{payload[0].value} links</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="p-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-zinc-950 red-thread pb-3">
          Dashboard
        </h1>
        <p className="text-sm text-zinc-500 mt-4">
          Analytics overview of your UTM link performance
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12 text-zinc-500">Loading analytics...</div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Total Links */}
            <div className="stat-card" data-testid="total-links-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Total Links Created
                  </p>
                  <p className="text-5xl font-bold text-zinc-950" data-testid="total-links-value">
                    {stats.total_links}
                  </p>
                </div>
                <div className="w-12 h-12 bg-[#AE132A] flex items-center justify-center">
                  <Link2 className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            {/* Top Source */}
            <div className="stat-card" data-testid="top-source-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Most Popular Source
                  </p>
                  <p className="text-3xl font-bold text-zinc-950 capitalize" data-testid="top-source-value">
                    {topSource ? topSource.source : "N/A"}
                  </p>
                  {topSource && (
                    <p className="text-sm text-zinc-500 mt-1">
                      {topSource.count} links
                    </p>
                  )}
                </div>
                <div className="w-12 h-12 bg-zinc-950 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>

            {/* This Week */}
            <div className="stat-card" data-testid="this-week-card">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-2">
                    Links This Week
                  </p>
                  <p className="text-5xl font-bold text-zinc-950" data-testid="this-week-value">
                    {stats.links_last_7_days.reduce((sum, day) => sum + day.count, 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-zinc-200 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-zinc-700" />
                </div>
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Source Distribution - Pie Chart */}
            <div className="border border-zinc-200 bg-white p-6" data-testid="source-pie-chart">
              <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-6">
                Source Distribution
              </h3>
              {stats.source_distribution.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.source_distribution}
                        dataKey="count"
                        nameKey="source"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        innerRadius={60}
                        paddingAngle={2}
                        label={({ source, percent }) =>
                          `${source} (${(percent * 100).toFixed(0)}%)`
                        }
                        labelLine={{ stroke: "#71717a", strokeWidth: 1 }}
                      >
                        {stats.source_distribution.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-zinc-400">
                  No data available
                </div>
              )}
            </div>

            {/* Last 7 Days - Bar Chart */}
            <div className="border border-zinc-200 bg-white p-6" data-testid="weekly-bar-chart">
              <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-6">
                Links Created (Last 7 Days)
              </h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.links_last_7_days} barSize={40}>
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      tick={{ fontSize: 12, fill: "#71717a" }}
                      axisLine={{ stroke: "#e4e4e7" }}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#71717a" }}
                      axisLine={false}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" fill="#AE132A" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Source Legend */}
          {stats.source_distribution.length > 0 && (
            <div className="mt-8 border border-zinc-200 bg-white p-6">
              <h3 className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-4">
                All Sources
              </h3>
              <div className="flex flex-wrap gap-4">
                {stats.source_distribution.map((item, index) => (
                  <div key={item.source} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <span className="text-sm text-zinc-700 capitalize">
                      {item.source}
                    </span>
                    <span className="text-xs text-zinc-400">({item.count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
